import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GlobalContextMenu.css';

const MIN_MARGIN = 8;

const isTextInput = (element) => {
  if (!(element instanceof HTMLInputElement)) {
    return false;
  }

  const blockedTypes = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit'
  ]);

  return !blockedTypes.has(element.type) && !element.readOnly && !element.disabled;
};

const isEditableElement = (element) => {
  if (!element) {
    return false;
  }

  if (isTextInput(element) || element instanceof HTMLTextAreaElement) {
    return !element.readOnly && !element.disabled;
  }

  return element instanceof HTMLElement && element.isContentEditable;
};

const getEditableTarget = (preferredTarget) => {
  if (isEditableElement(preferredTarget)) {
    return preferredTarget;
  }

  if (isEditableElement(document.activeElement)) {
    return document.activeElement;
  }

  return null;
};

const getCopyValue = (preferredTarget) => {
  if (isTextInput(preferredTarget) || preferredTarget instanceof HTMLTextAreaElement) {
    const start = preferredTarget.selectionStart ?? 0;
    const end = preferredTarget.selectionEnd ?? 0;
    return start === end ? '' : preferredTarget.value.slice(start, end);
  }

  const selection = window.getSelection();
  return selection ? selection.toString() : '';
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const helper = document.createElement('textarea');
  helper.value = text;
  helper.setAttribute('readonly', '');
  helper.style.position = 'fixed';
  helper.style.opacity = '0';
  document.body.appendChild(helper);
  helper.select();
  document.execCommand('copy');
  document.body.removeChild(helper);
};

const insertContent = (target, text) => {
  if (!target || !text) {
    return;
  }

  if (isTextInput(target) || target instanceof HTMLTextAreaElement) {
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;

    target.focus();
    target.setRangeText(text, start, end, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }

  if (target instanceof HTMLElement && target.isContentEditable) {
    target.focus();

    if (document.execCommand) {
      const inserted = document.execCommand('insertText', false, text);
      if (inserted) {
        return;
      }
    }

    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

function GlobalContextMenu({ onReturnToMenu }) {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [menuState, setMenuState] = useState({
    isOpen: false,
    x: MIN_MARGIN,
    y: MIN_MARGIN,
    target: null,
    canCopy: false,
    canPaste: false
  });

  const closeMenu = useCallback(() => {
    setMenuState((currentState) => (
      currentState.isOpen ? { ...currentState, isOpen: false } : currentState
    ));
  }, []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      event.preventDefault();

      if (menuRef.current?.contains(event.target)) {
        return;
      }

      const target = event.target;
      const editableTarget = getEditableTarget(target);

      setMenuState({
        isOpen: true,
        x: event.clientX || MIN_MARGIN,
        y: event.clientY || MIN_MARGIN,
        target,
        canCopy: Boolean(getCopyValue(target)),
        canPaste: Boolean(editableTarget)
      });
    };

    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) {
        return;
      }

      closeMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!menuState.isOpen || !menuRef.current) {
      return;
    }

    const { offsetWidth, offsetHeight } = menuRef.current;
    const nextX = Math.min(menuState.x, window.innerWidth - offsetWidth - MIN_MARGIN);
    const nextY = Math.min(menuState.y, window.innerHeight - offsetHeight - MIN_MARGIN);
    const safeX = Math.max(MIN_MARGIN, nextX);
    const safeY = Math.max(MIN_MARGIN, nextY);

    if (safeX !== menuState.x || safeY !== menuState.y) {
      setMenuState((currentState) => ({
        ...currentState,
        x: safeX,
        y: safeY
      }));
    }
  }, [menuState.isOpen, menuState.x, menuState.y]);

  const handleCopy = async () => {
    const text = getCopyValue(menuState.target);

    if (!text) {
      closeMenu();
      return;
    }

    try {
      await copyText(text);
    } catch (error) {
      // Ignore clipboard failures and simply close the menu.
    }

    closeMenu();
  };

  const handlePaste = async () => {
    const editableTarget = getEditableTarget(menuState.target);

    if (!editableTarget || !navigator.clipboard?.readText) {
      closeMenu();
      return;
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      insertContent(editableTarget, clipboardText);
    } catch (error) {
      // Ignore clipboard failures and simply close the menu.
    }

    closeMenu();
  };

  const handleReturnToMenu = () => {
    closeMenu();
    if (onReturnToMenu) {
      onReturnToMenu();
    }
    navigate('/');
  };

  if (!menuState.isOpen) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="global-context-menu"
      style={{
        left: `${menuState.x}px`,
        top: `${menuState.y}px`
      }}
      role="menu"
      aria-label="Menu contextuel"
    >
      <button
        type="button"
        className="global-context-menu__item"
        onClick={handleCopy}
        disabled={!menuState.canCopy}
      >
        Copier
      </button>
      <button
        type="button"
        className="global-context-menu__item"
        onClick={handlePaste}
        disabled={!menuState.canPaste}
      >
        Coller
      </button>
      <button
        type="button"
        className="global-context-menu__item"
        onClick={handleReturnToMenu}
      >
        Retour au menu
      </button>
    </div>
  );
}

export default GlobalContextMenu;
